<template>
    <div class="container max-w-3xl mx-auto mt-2 md:mt-8 pt-8 md:pb-8" v-if="appointmentData">
        <div class="w-full md:w-1/2 px-4 lg:px-0">
            <div class="mb-4 text-center md:hidden">
                <appointment-info />
            </div>
            <div class="mb-2 md:hidden">
                <changeAppointmentButton />
            </div>

            <h2 class="heading">
                Your details
                <span class="text-sm text-gray-darker pl-2">* Required fields</span>
            </h2>
            <form ref="form" @submit.prevent="submit">
                <div class="form-field-group">
                    <div class="form-group small-form-group">
                        <label for="patient-title-input" class="sr-only">
                            Title *
                        </label>

                        <custom-select
                                v-model="title"
                                id="patient-title-input"
                                mod="small"
                                :options="titles"
                                :required="true"
                                @input="handleCustomSelectChange('title', $event)"
                        />

                        <div v-if="errors.title" class="form-error">{{ errors.title }}</div>
                    </div>

                    <div class="form-group">
                        <label for="patient-first-name-input" class="sr-only">
                            First Name *
                        </label>

                        <input id="patient-first-name-input"
                            name="firstName"
                            v-model="firstName"
                            class="form-control"
                            placeholder="First name *"
                            required
                            maxlength="60"
                            @blur="clearError"
                            @change="handleFieldChange"
                        />

                        <div v-if="errors.firstName" class="form-error">{{ errors.firstName }}</div>
                    </div>

                    <div class="form-group">
                        <label for="patient-last-name-input" class="sr-only">
                            Last Name *
                        </label>

                        <input id="patient-last-name-input"
                            name="lastName"
                            v-model="lastName"
                            class="form-control"
                            placeholder="Last name *"
                            required
                            maxlength="60"
                            @blur="clearError"
                            @change="handleFieldChange"
                        />

                        <div v-if="errors.lastName" class="form-error">{{ errors.lastName }}</div>
                    </div>
                </div>

                <div class="form-field-group"> 
                    <div class="form-group">
                        <label for="patient-phone-input" class="sr-only">
                            Phone *
                        </label>

                        <input id="patient-phone-input"
                            name="phone"
                            v-model="phone"
                            class="form-control"
                            placeholder="Phone *"
                            required
                            maxlength="20"
                            @blur="clearError"
                            @change="handleFieldChange"
                        />

                        <div v-if="errors.phone" class="form-error">{{ errors.phone }}</div>
                    </div>

                    <div class="form-group">
                        <label for="patient-email-input" class="sr-only">
                            Email *
                        </label>

                        <input id="patient-email-input"
                            type="email"
                            name="email"
                            v-model="email"
                            class="form-control"
                            placeholder="Email *"
                            @blur="clearError"
                            @change="handleFieldChange"
                            required
                            maxlength="50"
                        />

                        <div v-if="errors.email" class="form-error">{{ errors.email }}</div>
                    </div>
                </div>

                <div class="form-group">
                    <input type="checkbox"
                           class="checkbox"
                           id="patient-marketing-terms"
                           name="marketing"
                           value="1"
                           v-model="marketing"
                           @change="clearError"
                    />

                    <label for="patient-marketing-terms" :class="{'text-red': !!errors.marketing}">
                        (Optional) I would like to receive communication from Ascenti about exclusive offers, products and services
                    </label>
                </div>

                <appointment-total class="mb-4 md:hidden" />

                <div class="form-group">
                    <button type="submit" role="button" class="btn btn-primary mr-2">
                        Continue
                    </button>
                </div>

                <change-appointment-button class="md:hidden" />
            </form>
        </div>

        <div class="hidden md:block w-1/2 mb-8 pl-20 pr-4 lg:pr-0 text-center">
            <appointment-info />

            <reserved-countdown />

            <appointment-total />
        </div>

        <add-voucher />

        <reserve-same-appointment />
    </div>
</template>

<script>
    import { mapActions, mapState} from 'vuex';

    import AddVoucher from './Modals/AddVoucher';
    import AppointmentInfo from './Cards/AppointmentInfo';
    import AppointmentTotal from "./Cards/AppointmentTotal";
    import ChangeAppointmentButton from "./Cards/ChangeAppointmentButton";
    import CustomSelect from '../Form/Element/CustomSelect';
    import ReservedCountdown from './Cards/ReservedCountdown';
    import ReserveSameAppointment from '../Appointments/ReserveSameAppointment';

    export default {
        name: 'patient-details',

        components: {
            AddVoucher,
            AppointmentInfo,
            AppointmentTotal,
            ChangeAppointmentButton,
            CustomSelect,
            ReservedCountdown,
            ReserveSameAppointment,
        },

        computed: {
            ...mapState('appointment', [
                'appointmentData',
            ]),

            ...mapState('checkout', [
                'patientDetails',
            ]),
        },

        data() {
            return {
                title: null,
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                marketing: false,

                submitting: false,

                titles: [
                    { value: null, label: 'Title *' },
                    { value: 'mr', label: 'Mr.' },
                    { value: 'mrs', label: 'Mrs.' },
                    { value: 'miss', label: 'Miss.' },
                    { value: 'ms', label: 'Ms.' },
                    { value: 'dr', label: 'Dr.' },
                    { value: 'other', label: 'Other' },
                ],

                errors: {},

                requiredFields: [
                    'title',
                    'firstName',
                    'lastName',
                    'phone',
                    'email',
                ],
            };
        },

        mounted() {
            this.$gtm.trackView('Booking Your Details', '/booking/your-details');

            this.$scrollTo('#app');

            this.title = this.patientDetails.title;
            this.firstName = this.patientDetails.firstName;
            this.lastName = this.patientDetails.lastName;
            this.phone = this.patientDetails.phone;
            this.email = this.patientDetails.email;
            this.marketing = this.patientDetails.marketing;

            this.$watch('title', this.syncStoreValues);
            this.$watch('firstName', this.syncStoreValues);
            this.$watch('lastName', this.syncStoreValues);
            this.$watch('phone', this.syncStoreValues);
            this.$watch('email', this.syncStoreValues);
            this.$watch('marketing', this.syncStoreValues);
        },

        methods: {
            ...mapActions('checkout', [
                'setPatientDetails',
                'setPatientDetailsValid',
                'setPatientDetailsInvalid',
            ]),

            handleCustomSelectChange(field, value) {
                this.$delete(this.errors, field);

                if (value) {
                    this.trackFieldChange(field);
                }
            },

            handleFieldChange(e) {
                if (e.target.value) {
                    this.trackFieldChange(e.target.name, e.target.value);
                }
            },

            trackFieldChange(field) {
                this.$gtm.trackEvent({
                    event: 'fieldcomplete',
                    fieldname: field,
                });
            },

            clearError(e) {
                this.$delete(this.errors, e.target.name);
            },

            syncStoreValues() {
                this.setPatientDetails({
                    title: this.title,
                    firstName: window._.trim(this.firstName),
                    lastName: window._.trim(this.lastName),
                    phone: window._.trim(this.phone),
                    email: window._.trim(this.email).toLowerCase(),
                    marketing: this.marketing,
                });

                if (this.isValid()) {
                    this.setPatientDetailsValid();
                    return true;
                } else {
                    this.setPatientDetailsInvalid();
                    return false;
                }
            },

            isValid() {
                let hasError = false, value, hasValue;

                for (let i in this.requiredFields) {
                    value = this[this.requiredFields[i]];
                    hasValue = typeof value === 'boolean' ? !!value : window._.trim(value);

                    if (!hasValue) {
                        if (this.submitting) {
                            this.$set(this.errors, this.requiredFields[i], 'Please fill in this field.');
                        }

                        hasError = true;
                    } else {
                        this.$delete(this.errors, this.requiredFields[i]);
                    }
                }

                if (this.email && !this.validateEmail(this.email)) {
                    if (this.submitting) {
                        this.$set(this.errors, 'email', 'Please double-check you have entered your email correctly.');
                    }

                    hasError = true;
                }

                return !hasError;
            },

            submit() {
                if (this.submitting) {
                    return;
                }

                this.submitting = true;

                const isValid = this.syncStoreValues();

                this.submitting = false;

                if (!isValid) {
                    return false;
                }

                this.dispatch('checkout.tab', 'injury');
            },
        },
    }
</script>

<style lang="scss" scoped>
    .container {
        @apply flex flex-col;

        @screen md {
            @apply flex-row justify-between;
        }
    }
</style>
